import { SEARCH } from '../config/app-config';
import { getOidcClient } from '../auth/oidc-client';

export const ENTERPRISE_SEARCH_AUDIENCE = 'search_frontend';
export const ENTERPRISE_SEARCH_PAGE_SIZE = 20;

export const ENTERPRISE_SEARCH_ENDPOINTS = Object.freeze({
    health: '/api/v1/health',
    search: '/api/v1/search',
    facets: '/api/v1/facets',
    suggest: '/api/v1/suggest',
});

export function buildEnterpriseSearchRequest({
    query,
    filters = {},
    size = ENTERPRISE_SEARCH_PAGE_SIZE,
    from = 0,
    userLanguage = 'en',
}) {
    return {
        query: String(query || '').trim(),
        filters,
        size,
        from,
        use_semantic: true,
        user_language: userLanguage,
    };
}

async function getOptionalAuthHeaders() {
    try {
        const oidcClient = getOidcClient();
        if (!oidcClient) return {};
        const user = await oidcClient.getUser();
        if (user && !user.expired && user.access_token) {
            return { Authorization: `Bearer ${user.access_token}` };
        }
    } catch (error) {
        console.warn('Enterprise search could not read the current user session:', error);
    }
    return {};
}

async function requestJson(path, options = {}) {
    if (!SEARCH.apiBaseUrl) {
        throw new Error('Enterprise search API is not configured.');
    }

    const authHeaders = await getOptionalAuthHeaders();
    const url = `${SEARCH.apiBaseUrl}${path}`;
    const requestOptions = {
        ...options,
        headers: {
            ...(options.body ? { 'Content-Type': 'application/json' } : {}),
            ...authHeaders,
            ...(options.headers || {}),
        },
    };
    let response = await fetch(url, requestOptions);

    // The docs and enterprise apps use separate OIDC clients. If the enterprise
    // API does not accept the current docs token, retain public search behavior.
    if (response.status === 401 && authHeaders.Authorization) {
        const anonymousHeaders = { ...requestOptions.headers };
        delete anonymousHeaders.Authorization;
        response = await fetch(url, { ...requestOptions, headers: anonymousHeaders });
    }

    if (!response.ok) {
        let detail = '';
        try {
            const errorBody = await response.json();
            detail = errorBody.detail || errorBody.message || '';
        } catch (_) {
            // The response did not contain a JSON error body.
        }
        throw new Error(detail || `Enterprise search request failed (HTTP ${response.status}).`);
    }

    return response.json();
}

export function searchEnterprise(request, signal) {
    return requestJson(ENTERPRISE_SEARCH_ENDPOINTS.search, {
        method: 'POST',
        body: JSON.stringify(buildEnterpriseSearchRequest(request)),
        signal,
    });
}

export function suggestEnterprise(query, userLanguage, signal) {
    const params = new URLSearchParams({
        q: String(query || '').trim(),
        size: '5',
        user_language: userLanguage || 'en',
        audience: ENTERPRISE_SEARCH_AUDIENCE,
    });

    return requestJson(`${ENTERPRISE_SEARCH_ENDPOINTS.suggest}?${params}`, { signal });
}

export function fetchEnterpriseFacets(signal) {
    const params = new URLSearchParams({ audience: ENTERPRISE_SEARCH_AUDIENCE });
    return requestJson(`${ENTERPRISE_SEARCH_ENDPOINTS.facets}?${params}`, { signal });
}
